'use client';

import type { Provider } from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { ResourceTypes } from './resourceTypes';

const wipResourceTypes = [
  ResourceType.ResourceTypeUnspecified,
  ResourceType.AwsEfs,
  ResourceType.AwsFsx,
  ResourceType.AwsKeyspacesTable,
  ResourceType.AwsCassandraCluster,
  ResourceType.GcpCassandraCluster,
  ResourceType.AzureCassandraCluster,
  ResourceType.AwsNeptune,
  ResourceType.AzureFileShare,
  ResourceType.AzureDisk,
  ResourceType.AzureSapHanaVm,
  ResourceType.GcpSapHanaVm,
  ResourceType.AzureSqlDatabase,
  ResourceType.AzureSqlManagedInstance,
  ResourceType.AzureMysql,
  ResourceType.AzureCosmosdbMongodb,
  ResourceType.AzureCosmosdbNosql,
  ResourceType.AzureAksNamespace,
  ResourceType.GcpGkeNamespace,
  ResourceType.GcpCloudFirestore,
  ResourceType.GoogleWorkspaceResource,
  ResourceType.Microsoft365Resource,
];

export const useSupportedResourceTypes = (
  cloudProviders: Provider[] = []
): ResourceType[] => {
  const { isDemo, isDev } = useEnvironment();
  const {
    AzureSqlDatabaseSupport,
    SapHanaPitrSupport,
    AwsKeyspacesSupport,
    CassandraSupport,
    AzureFilesSupport,
    GoogleWorkspaceSupport,
    m365BackupConsole,
  } = useFeatureFlags();

  return useMemo(() => {
    const ffResources = [
      ResourceType.AwsFsx,
      ResourceType.AwsEfs,
      ...(AwsKeyspacesSupport ? [ResourceType.AwsKeyspacesTable] : []),
      ...(CassandraSupport
        ? [
            ResourceType.AwsCassandraCluster,
            ResourceType.GcpCassandraCluster,
            ResourceType.AzureCassandraCluster,
          ]
        : []),
      ...(AzureSqlDatabaseSupport ? [ResourceType.AzureSqlDatabase] : []),
      ...(SapHanaPitrSupport
        ? [ResourceType.AzureSapHanaVm, ResourceType.GcpSapHanaVm]
        : []),
      ResourceType.AwsDocumentdb,
      ResourceType.AwsNeptune,
      ...(AzureFilesSupport ? [ResourceType.AzureFileShare] : []),
      ...(GoogleWorkspaceSupport ? [ResourceType.GoogleWorkspaceResource] : []),
      ...(m365BackupConsole ? [ResourceType.Microsoft365Resource] : []),
    ];

    return Object.values(ResourceType).filter(
      (x) =>
        x !== ResourceType.ResourceTypeUnspecified &&
        (cloudProviders.length
          ? cloudProviders.includes(ResourceTypes[x]?.provider)
          : true) &&
        (isDemo ||
          isDev ||
          !wipResourceTypes.includes(x) ||
          ffResources.includes(x))
    );
  }, [
    AwsKeyspacesSupport,
    CassandraSupport,
    AzureSqlDatabaseSupport,
    SapHanaPitrSupport,
    AzureFilesSupport,
    GoogleWorkspaceSupport,
    m365BackupConsole,
    isDemo,
    isDev,
    cloudProviders,
  ]);
};
